import type { ReactNode, SelectHTMLAttributes } from 'react';
import { cn } from '../lib/cn.js';
import {
  controlBorderClass,
  controlDisabled,
  controlFocus,
  controlHover,
  controlLoading,
  controlSurface,
  errorMessageId,
} from '../primitives/shared.js';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  loading?: boolean;
  /** Shown when the option set resolved with no members — PRM-06 empty state. */
  emptyMessage?: string;
  children: ReactNode;
}

/** PRM-06 Select — choose one from many. Native control; renders all eight states. */
export function Select({
  label,
  error,
  loading,
  emptyMessage = 'No options available',
  children,
  id,
  className,
  disabled,
  required,
  'aria-describedby': ariaDescribedBy,
  ...props
}: SelectProps) {
  const selectId = id ?? props.name;
  const errorId = errorMessageId(selectId);
  const describedBy =
    [ariaDescribedBy, error ? errorId : undefined].filter(Boolean).join(' ') || undefined;
  const isEmpty = Array.isArray(children)
    ? children.flat().filter(Boolean).length === 0
    : !children;

  return (
    <label htmlFor={selectId} className="flex flex-col gap-field-label">
      {label ? (
        <span className="text-label font-medium text-default">
          {label}
          {required ? <span className="sr-only"> (required)</span> : null}
        </span>
      ) : null}
      <select
        id={selectId}
        data-primitive="PRM-06"
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy}
        aria-disabled={disabled || isEmpty || undefined}
        aria-busy={loading || undefined}
        disabled={disabled || isEmpty}
        required={required}
        className={cn(
          controlSurface,
          'min-h-control',
          controlBorderClass(error),
          controlHover,
          controlFocus,
          controlDisabled,
          controlLoading,
          'active:border-line-focus',
          'disabled:cursor-not-allowed disabled:bg-off disabled:text-disabled',
          className,
        )}
        {...props}
      >
        {isEmpty ? <option value="">{emptyMessage}</option> : children}
      </select>
      {error ? (
        <span id={errorId} role="alert" className="text-body-sm text-danger">
          {error}
        </span>
      ) : null}
    </label>
  );
}
